import { dom } from "../dom";
import { state, type StoreyLevel } from "../state";
import { ifcLoader } from "../viewer/scene";
import { ifcTypeLabel } from "../i18n";
import { getElementMark, getIfcName } from "./props";
import { setFilterControlsEnabled, updateFilterSelectOptions } from "./filters";
import { loadMetaFromCache, saveMetaToCache } from "./metaCache";
import { getActiveFragmentsModel } from "../fragments/engine";

import { getGeometryExpressIds } from "../fragments/helpers";

export { getGeometryExpressIds };

function readElevation(row: any): number {
  const raw = row?.Elevation;
  const value = raw && typeof raw === "object" && "value" in raw ? raw.value : raw;
  const num = typeof value === "number" ? value : Number(value);
  return Number.isFinite(num) ? num : 0;
}

function spatialNodeId(node: any): number {
  const id = Number(node?.localId ?? node?.expressID);
  return Number.isFinite(id) ? id : -1;
}

function spatialNodeType(node: any): string {
  return String(node?.category ?? node?.type ?? "");
}

function unwrapNum(v: unknown): number | null {
  if (typeof v === "number") {
    return v;
  }
  if (v && typeof v === "object" && "value" in (v as object)) {
    const inner = (v as { value: unknown }).value;
    return typeof inner === "number" ? inner : null;
  }
  return null;
}

/**
 * localId непосредственного «родителя» элемента: сначала пространственный
 * контейнер (`ContainedInStructure` → этаж/пространство), иначе сборка
 * (`Decomposes` → curtain wall / stair / …). Используется для подъёма
 * по цепочке вплоть до этажа.
 */
function relationParentId(row: any): number | null {
  const contained = row?.ContainedInStructure;
  if (Array.isArray(contained) && contained[0]) {
    const id = unwrapNum(contained[0]._localId);
    if (id !== null) {
      return id;
    }
  }
  const decomposes = row?.Decomposes;
  if (Array.isArray(decomposes) && decomposes[0]) {
    const id = unwrapNum(decomposes[0]._localId);
    if (id !== null) {
      return id;
    }
  }
  return null;
}

export function assignStoreyMappingFromTree(
  node: any,
  currentStorey: number | null,
  storeyMap: Map<number, number | null>,
  typeMap?: Map<number, string>,
): void {
  if (!node) {
    return;
  }
  const nodeType = spatialNodeType(node);
  const nodeId = spatialNodeId(node);
  const nextStorey = nodeType === "IFCBUILDINGSTOREY" ? nodeId : currentStorey;
  if (nodeId > 0) {
    storeyMap.set(nodeId, nextStorey ?? null);
    if (typeMap && nodeType && nodeType !== "web-ifc-type-unknown") {
      typeMap.set(nodeId, nodeType);
    }
  }
  const children = node.children || [];
  for (let i = 0; i < children.length; i++) {
    assignStoreyMappingFromTree(children[i], nextStorey, storeyMap, typeMap);
  }
}

function applyMetaToUi(storeyNames: Iterable<string>, typeNames: Iterable<string>): void {
  updateFilterSelectOptions(
    dom.filterStoreySelect,
    [...new Set(storeyNames)].filter(Boolean).sort((a, b) => a.localeCompare(b)),
    "Все этажи",
  );
  // Типы сортируем по локализованной подписи, чтобы список читался по-русски.
  updateFilterSelectOptions(
    dom.filterTypeSelect,
    [...new Set(typeNames)]
      .filter(Boolean)
      .sort((a, b) => ifcTypeLabel(a).localeCompare(ifcTypeLabel(b))),
    "Все типы",
    ifcTypeLabel,
  );
  setFilterControlsEnabled(true);
}

/**
 * Строит `state.elementMeta` для активной модели (только элементы С ГЕОМЕТРИЕЙ).
 *
 * Раньше метаданные собирались поэлементно (`await getItemProperties` на каждый
 * id), что на больших моделях занимало минуты — фильтр фактически не наполнялся.
 * Теперь всё читается ПАКЕТНО за несколько вызовов воркера:
 *  - типы: `model.getItemsOfCategories()` (один вызов даёт id→категория);
 *  - этажи: id+имена через категорию IFCBUILDINGSTOREY, привязка элемент→этаж
 *    через relation-цепочку ContainedInStructure/Decomposes;
 *  - марки: `model.getItemsData(chunk, …)` чанками.
 *
 * При наличии `cacheSignature` сначала пробуем кэш IndexedDB (моментально).
 */
export async function buildElementMetaCache(
  cacheSignature: string | null = null,
  onProgress?: (percent: number) => void,
): Promise<void> {
  state.elementMeta.clear();
  state.storeyLevels = [];
  if (!state.ifcModel) {
    return;
  }

  if (cacheSignature) {
    const cached = await loadMetaFromCache(cacheSignature);
    if (cached) {
      for (const [id, entry] of cached.entries) {
        state.elementMeta.set(id, entry);
      }
      applyMetaToUi(cached.storeyNames, cached.typeNames);
      state.storeyLevels = [...(cached.storeyLevels || [])].sort((a, b) => a.elevation - b.elevation);
      onProgress?.(100);
      return;
    }
  }

  onProgress?.(0);
  const ids = getGeometryExpressIds();
  const modelId = state.ifcModel.modelID;
  const model = state.fragmentsModelId
    ? getActiveFragmentsModel(state.fragmentsModelId)
    : undefined;

  // 1) Типы (IFC-классы) — пакетно по категориям. Один вызов воркера даёт
  //    карту id → "IFCWALL"/"IFCDOOR"/… для всех элементов модели.
  const typeById = new Map<number, string>();
  if (model) {
    try {
      const byCategory = await model.getItemsOfCategories([/.+/]);
      for (const [category, categoryIds] of Object.entries(byCategory)) {
        if (!category) {
          continue;
        }
        for (const id of categoryIds as number[]) {
          typeById.set(id, category);
        }
      }
    } catch (_) {
      /* fallback на типы из дерева ниже */
    }
  }

  // 2) Этажи: реальные localId и имена — через категорию IFCBUILDINGSTOREY.
  //    Пространственное дерево Fragments здесь ненадёжно: узлы этажей часто
  //    приходят с localId=null, из-за чего ВСЕ этажи схлопывались в один
  //    безымянный пункт, и фильтр этажности работал неверно.
  const storeyNameById = new Map<number, string>();
  const storeyLevelById = new Map<number, StoreyLevel>();
  const storeySet = new Set<number>();
  if (model) {
    try {
      const byStorey = await model.getItemsOfCategories([/^IFCBUILDINGSTOREY$/]);
      const storeyIds = (byStorey["IFCBUILDINGSTOREY"] || []) as number[];
      if (storeyIds.length > 0) {
        const rows = await model.getItemsData(storeyIds, { attributesDefault: true });
        for (let i = 0; i < storeyIds.length; i++) {
          const row: any = rows[i] || {};
          const byName = getIfcName(row);
          const byLong = typeof row?.LongName?.value === "string" ? row.LongName.value : "";
          const name = byName !== "Без имени" ? byName : byLong || `Этаж #${storeyIds[i]}`;
          storeyNameById.set(storeyIds[i], name);
          storeyLevelById.set(storeyIds[i], {
            id: storeyIds[i],
            name,
            elevation: readElevation(row),
          });
          storeySet.add(storeyIds[i]);
        }
      }
    } catch (_) {
      /* без этажей фильтр этажности просто останется пустым */
    }
  }

  // Типы из дерева как fallback, если getItemsOfCategories не дал результата.
  if (typeById.size === 0) {
    try {
      const spatial = await ifcLoader.ifcManager.getSpatialStructure(modelId);
      assignStoreyMappingFromTree(spatial, null, new Map(), typeById);
    } catch (_) {
      /* optional */
    }
  }

  // 3) Один пакетный проход: марки (Tag/Name) + непосредственный родитель
  //    (ContainedInStructure → этаж/пространство, иначе Decomposes → сборка).
  const markById = new Map<number, string>();
  const parentRef = new Map<number, number>();
  if (model && ids.length > 0) {
    const CHUNK = 500;
    for (let i = 0; i < ids.length; i += CHUNK) {
      const chunk = ids.slice(i, i + CHUNK);
      try {
        const rows = await model.getItemsData(chunk, {
          attributesDefault: true,
          relations: {
            ContainedInStructure: { attributes: true, relations: false },
            Decomposes: { attributes: true, relations: false },
          },
        });
        for (let j = 0; j < chunk.length; j++) {
          const row: any = rows[j];
          markById.set(chunk[j], getElementMark(row) || "");
          const pid = relationParentId(row);
          if (pid !== null) {
            parentRef.set(chunk[j], pid);
          }
        }
      } catch (_) {
        /* пропускаем чанк — атрибуты необязательны */
      }
      onProgress?.(Math.floor(((i + chunk.length) / ids.length) * 95));
    }
  }

  // 4) Разрешение этажа для каждого элемента: поднимаемся по цепочке
  //    родителей (пространство → этаж; сборка → … → этаж), догружая ссылки
  //    промежуточных узлов, пока цепочка не упрётся в этаж.
  const storeyIdByElement = new Map<number, number>();
  if (model && storeySet.size > 0) {
    const loadParentRefs = async (targets: number[]): Promise<void> => {
      const need = targets.filter((id) => !parentRef.has(id) && !storeySet.has(id));
      const CHUNK = 500;
      for (let i = 0; i < need.length; i += CHUNK) {
        const chunk = need.slice(i, i + CHUNK);
        try {
          const rows = await model.getItemsData(chunk, {
            attributesDefault: false,
            relations: {
              ContainedInStructure: { attributes: true, relations: false },
              Decomposes: { attributes: true, relations: false },
            },
          });
          for (let j = 0; j < chunk.length; j++) {
            const pid = relationParentId(rows[j]);
            if (pid !== null) {
              parentRef.set(chunk[j], pid);
            }
          }
        } catch (_) {
          /* ignore */
        }
      }
    };

    for (let level = 0; level < 10; level++) {
      const pending = new Set<number>();
      for (const id of ids) {
        let cur: number | undefined = id;
        let steps = 0;
        while (cur !== undefined && steps < 24) {
          if (storeySet.has(cur)) {
            break;
          }
          const next = parentRef.get(cur);
          if (next === undefined) {
            pending.add(cur);
            break;
          }
          cur = next;
          steps++;
        }
      }
      if (pending.size === 0) {
        break;
      }
      const before = parentRef.size;
      await loadParentRefs([...pending]);
      if (parentRef.size === before) {
        break; // прогресса нет — выходим, чтобы не зациклиться
      }
    }

    for (const id of ids) {
      let cur: number | undefined = id;
      let steps = 0;
      while (cur !== undefined && steps < 24) {
        if (storeySet.has(cur)) {
          storeyIdByElement.set(id, cur);
          break;
        }
        cur = parentRef.get(cur);
        steps++;
      }
    }
  }

  // 5) Сборка meta только для элементов с геометрией.
  const storeyNames = new Set<string>();
  const typeNames = new Set<string>();
  for (const id of ids) {
    const type = typeById.get(id) || "";
    const mark = markById.get(id) || "";
    const sid = storeyIdByElement.get(id);
    const storeyName = sid !== undefined ? storeyNameById.get(sid) || "" : "";
    state.elementMeta.set(id, { type, mark, storeyName });
    if (storeyName) {
      storeyNames.add(storeyName);
    }
    if (type && type !== "web-ifc-type-unknown") {
      typeNames.add(type);
    }
  }

  applyMetaToUi(storeyNames, typeNames);
  state.storeyLevels = [...storeyLevelById.values()].sort((a, b) => a.elevation - b.elevation);
  onProgress?.(100);

  if (cacheSignature) {
    void saveMetaToCache(cacheSignature, {
      entries: [...state.elementMeta.entries()],
      storeyNames: [...storeyNames],
      storeyLevels: state.storeyLevels,
      typeNames: [...typeNames],
    });
  }
}
